import React, { useState } from "react";
import { Link } from "react-router-dom";
import "./Contact.css";
import Footer from "../Footer";
import { MailIcon, MessageIcon } from "../public/icons";
import { SUPPORT_EMAIL, SUPPORT_REPLY_WINDOW } from "../../constants/supportContact";

const TOPICS = [
  "Something isn't working",
  "A question about payments",
  "Help setting up my page",
  "Billing or my subscription",
  "A feature idea",
  "Something else",
];

// The public site has no backend endpoint for anonymous messages, so the form
// composes an email in the visitor's own mail app addressed to the support
// inbox.
export const buildSupportMailto = ({ name, email, topic, message, sendCopy }) => {
  const trimmedName = name.trim();
  const trimmedEmail = email.trim();
  const subject = `[${topic}] Message from ${trimmedName || "a Haastia visitor"}`;
  const body = [
    `Name: ${trimmedName}`,
    `Email: ${trimmedEmail}`,
    `Topic: ${topic}`,
    "",
    message.trim(),
  ].join("\n");

  const params = new URLSearchParams({ subject, body });
  if (sendCopy && trimmedEmail) params.set("cc", trimmedEmail);

  // URLSearchParams encodes spaces as "+", which mail clients show literally.
  return `mailto:${SUPPORT_EMAIL}?${params.toString().replace(/\+/g, "%20")}`;
};

const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    topic: TOPICS[0],
    message: "",
    sendCopy: false,
  });
  const [status, setStatus] = useState(null);

  const updateField = (field) => (event) => {
    const value = event.target.type === "checkbox" ? event.target.checked : event.target.value;
    setForm((current) => ({ ...current, [field]: value }));
    if (status) setStatus(null);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!form.message.trim()) {
      setStatus({ type: "error", text: "Tell us what happened so we know how to help." });
      return;
    }

    const href = buildSupportMailto(form);
    try {
      window.location.assign(href);
      setStatus({
        type: "success",
        text: `Your email app should now be open with this message addressed to ${SUPPORT_EMAIL}. If nothing opened, copy the text and send it to us directly.`,
      });
    } catch (_error) {
      setStatus({
        type: "error",
        text: `We couldn't open your email app. Please send your message to ${SUPPORT_EMAIL}.`,
      });
    }
  };

  return (
    <>
      <main className="contact-page">
        <section className="site-section site-section--hero contact-section">
          <span className="deco deco--2 contact-section__deco" aria-hidden="true" />
          <div className="site-inner">
            <div className="contact-grid">
              <div className="contact-copy">
                <p className="eyebrow">Contact</p>
                <h1>Write to us like a person.</h1>
                <p className="lead">
                  There's no ticket queue and no chatbot. Tell us what happened, add a screenshot
                  if you have one, and you'll hear back within {SUPPORT_REPLY_WINDOW}.
                </p>
                <div className="contact-cards">
                  <a className="panel info-row contact-card" href={`mailto:${SUPPORT_EMAIL}`}>
                    <span className="icon-circle">
                      <MailIcon size={20} />
                    </span>
                    <span>
                      <p>Email</p>
                      <strong>{SUPPORT_EMAIL}</strong>
                    </span>
                  </a>
                  <Link className="panel info-row contact-card" to="/help">
                    <span className="icon-circle icon-circle--sage">
                      <MessageIcon size={20} />
                    </span>
                    <span>
                      <p>Need a quick answer?</p>
                      <strong>Browse the Help Center</strong>
                    </span>
                  </Link>
                </div>
              </div>

              <form className="panel panel--elevated contact-form" onSubmit={handleSubmit} noValidate>
                <div className="contact-form__row">
                  <div className="field">
                    <label htmlFor="contact-name">Your name</label>
                    <input
                      className="input"
                      id="contact-name"
                      type="text"
                      name="name"
                      autoComplete="name"
                      placeholder="Nia Adeyemi"
                      value={form.name}
                      onChange={updateField("name")}
                    />
                  </div>
                  <div className="field">
                    <label htmlFor="contact-email">Email</label>
                    <input
                      className="input"
                      id="contact-email"
                      type="email"
                      name="email"
                      autoComplete="email"
                      placeholder="you@example.com"
                      value={form.email}
                      onChange={updateField("email")}
                    />
                  </div>
                </div>

                <div className="field">
                  <label htmlFor="contact-topic">What's this about?</label>
                  <select
                    className="input"
                    id="contact-topic"
                    name="topic"
                    value={form.topic}
                    onChange={updateField("topic")}
                  >
                    {TOPICS.map((topic) => (
                      <option key={topic} value={topic}>
                        {topic}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="field">
                  <label htmlFor="contact-message">Tell us what happened</label>
                  <textarea
                    className="input"
                    id="contact-message"
                    name="message"
                    placeholder="The more detail the better — what you were doing, what you expected, what you saw instead."
                    value={form.message}
                    onChange={updateField("message")}
                    required
                  />
                </div>

                <label className="checkbox-row" htmlFor="contact-send-copy">
                  <input
                    id="contact-send-copy"
                    type="checkbox"
                    name="sendCopy"
                    checked={form.sendCopy}
                    onChange={updateField("sendCopy")}
                  />
                  <span>Send me a copy of this message</span>
                </label>

                {status && (
                  <p
                    className={`form-status form-status--${status.type}`}
                    role={status.type === "error" ? "alert" : "status"}
                  >
                    {status.text}
                  </p>
                )}

                <button type="submit" className="btn btn-primary btn-block btn-md">
                  Send message
                </button>
                <p className="contact-form__note">We reply from a real inbox. No auto-responder.</p>
              </form>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default Contact;
